import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { ClipboardList, FolderPlus, Plus } from "lucide-react";
import {
	useDeleteScorecardCategory,
	useDeleteScorecardField,
	useReorderScorecardCategories,
	useReorderScorecardFields,
	useSaveScorecardCategory,
	useSaveScorecardField,
	useScorecardCategories,
	useScorecardFields,
} from "@/hooks/scorecard/useScorecardConfig";
import { CategoryManager } from "./CategoryManager";
import { CategoryFormDialog } from "./CategoryFormDialog";
import { FieldFormDialog } from "./FieldFormDialog";
import { FieldsTable } from "./FieldsTable";
import { ScorecardFieldsSkeleton } from "./ScorecardFieldsSkeleton";
import type {
	ScorecardCategory,
	ScorecardCategoryInput,
	ScorecardField,
	ScorecardFieldInput,
} from "@/types/scorecard";

/** Returns the two-row display_order swap for moving a row up or down, or null at the edges. */
function swapUpdates<T extends { id: string; display_order: number }>(
	rows: T[],
	row: T,
	direction: -1 | 1,
): { id: string; display_order: number }[] | null {
	const index = rows.findIndex((candidate) => candidate.id === row.id);
	const neighbor = rows[index + direction];
	if (index === -1 || !neighbor) return null;
	// Index-based values rather than swapped display_orders, so rows with
	// colliding orders (legacy data) still separate cleanly.
	return [
		{ id: row.id, display_order: index + direction },
		{ id: neighbor.id, display_order: index },
	];
}

/**
 * Admin tab for configuring the organization's candidate scorecard:
 * categories (sections) and the evaluation fields inside them.
 */
const ScorecardFieldsTab: React.FC = () => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const { currentOrganization } = useAuth();
	const organizationId = currentOrganization?.id;

	const fieldsQuery = useScorecardFields(organizationId);
	const categoriesQuery = useScorecardCategories(organizationId);
	const saveCategory = useSaveScorecardCategory(organizationId);
	const deleteCategory = useDeleteScorecardCategory(organizationId);
	const saveField = useSaveScorecardField(organizationId);
	const deleteField = useDeleteScorecardField(organizationId);
	const reorderFields = useReorderScorecardFields(organizationId);
	const reorderCategories = useReorderScorecardCategories(organizationId);

	const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
	const [editingField, setEditingField] = useState<ScorecardField | null>(null);
	const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
	const [editingCategory, setEditingCategory] = useState<ScorecardCategory | null>(null);

	const errorToast = (title: string) =>
		toast({
			title,
			description: t("admin.scorecardFields.errors.tryAgain", "Please try again."),
			variant: "destructive",
		});

	const handleSubmitField = async (input: ScorecardFieldInput) => {
		try {
			const maxOrder = Math.max(-1, ...fieldsQuery.fields.map((field) => field.display_order));
			await saveField.mutateAsync(
				input.id ? input : { ...input, display_order: maxOrder + 1 },
			);
			setFieldDialogOpen(false);
			toast({ title: t("admin.scorecardFields.toasts.fieldSaved", "Field saved") });
		} catch {
			errorToast(t("admin.scorecardFields.errors.saveFieldFailed", "Could not save the field"));
		}
	};

	const handleDeleteField = async (field: ScorecardField) => {
		try {
			await deleteField.mutateAsync(field.id);
			toast({ title: t("admin.scorecardFields.toasts.fieldDeleted", "Field deleted") });
		} catch {
			errorToast(t("admin.scorecardFields.errors.deleteFieldFailed", "Could not delete the field"));
		}
	};

	const handleSubmitCategory = async (input: ScorecardCategoryInput) => {
		try {
			const maxOrder = Math.max(
				-1,
				...categoriesQuery.categories.map((category) => category.display_order),
			);
			await saveCategory.mutateAsync(
				input.id ? input : { ...input, display_order: maxOrder + 1 },
			);
			setCategoryDialogOpen(false);
			toast({ title: t("admin.scorecardFields.toasts.categorySaved", "Category saved") });
		} catch {
			errorToast(t("admin.scorecardFields.errors.saveCategoryFailed", "Could not save the category"));
		}
	};

	const handleDeleteCategory = async (category: ScorecardCategory) => {
		try {
			await deleteCategory.mutateAsync(category.id);
			toast({ title: t("admin.scorecardFields.toasts.categoryDeleted", "Category deleted") });
		} catch {
			errorToast(t("admin.scorecardFields.errors.deleteCategoryFailed", "Could not delete the category"));
		}
	};

	const handleMoveField = (field: ScorecardField, direction: -1 | 1) => {
		const updates = swapUpdates(fieldsQuery.fields, field, direction);
		if (!updates) return;
		reorderFields.mutate(updates, {
			onError: () =>
				errorToast(t("admin.scorecardFields.errors.reorderFailed", "Could not reorder")),
		});
	};

	const handleMoveCategory = (category: ScorecardCategory, direction: -1 | 1) => {
		const updates = swapUpdates(categoriesQuery.categories, category, direction);
		if (!updates) return;
		reorderCategories.mutate(updates, {
			onError: () =>
				errorToast(t("admin.scorecardFields.errors.reorderFailed", "Could not reorder")),
		});
	};

	if (fieldsQuery.isLoading || categoriesQuery.isLoading) {
		return <ScorecardFieldsSkeleton />;
	}

	if (fieldsQuery.isError || categoriesQuery.isError) {
		return (
			<div className="flex flex-col items-center gap-3 py-12 text-center">
				<p className="text-sm text-muted-foreground">
					{t("admin.scorecardFields.errors.loadFailed", "The scorecard configuration could not be loaded.")}
				</p>
				<Button
					variant="outline"
					onClick={() => {
						fieldsQuery.refetch();
						categoriesQuery.refetch();
					}}
				>
					{t("admin.scorecardFields.errors.retry", "Retry")}
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-center justify-end gap-2">
				<Button variant="outline" onClick={() => { setEditingCategory(null); setCategoryDialogOpen(true); }}>
					<FolderPlus className="mr-2 h-4 w-4" />
					{t("admin.scorecardFields.categories.add", "Add category")}
				</Button>
				<Button onClick={() => { setEditingField(null); setFieldDialogOpen(true); }}>
					<Plus className="mr-2 h-4 w-4" />
					{t("admin.scorecardFields.addField", "Add field")}
				</Button>
			</div>

			<CategoryManager
				categories={categoriesQuery.categories}
				fields={fieldsQuery.fields}
				onEdit={(category) => {
					setEditingCategory(category);
					setCategoryDialogOpen(true);
				}}
				onDelete={handleDeleteCategory}
				onMove={handleMoveCategory}
			/>

			{fieldsQuery.fields.length === 0 ? (
				<EmptyState
					icon={ClipboardList}
					title={t("admin.scorecardFields.empty.title", "No scorecard fields yet")}
					description={t(
						"admin.scorecardFields.empty.description",
						"Add evaluation criteria here and they will appear on the Scorecard tab of every candidate profile.",
					)}
				/>
			) : (
				<FieldsTable
					fields={fieldsQuery.fields}
					onEdit={(field) => {
						setEditingField(field);
						setFieldDialogOpen(true);
					}}
					onDelete={handleDeleteField}
					onMove={handleMoveField}
				/>
			)}

			<FieldFormDialog
				open={fieldDialogOpen}
				onOpenChange={setFieldDialogOpen}
				field={editingField}
				categories={categoriesQuery.categories}
				existingFields={fieldsQuery.fields}
				onSubmit={handleSubmitField}
				saving={saveField.isPending}
			/>
			<CategoryFormDialog
				open={categoryDialogOpen}
				onOpenChange={setCategoryDialogOpen}
				category={editingCategory}
				onSubmit={handleSubmitCategory}
				saving={saveCategory.isPending}
			/>
		</div>
	);
};

export default ScorecardFieldsTab;
